import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SortableTableHeader } from '../components/ui/sortable-table-header';
import { Plus, MagnifyingGlass, PencilSimple, Trash, ShieldCheck, Key, Lightning, Play, WarningCircle, ArrowsClockwise, Calendar, Users, XCircle, CalendarBlank, UserPlus, CaretLeft, CaretRight, CaretUpDown, Check, User, Lock, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import InfoTooltip from '../components/InfoTooltip';
import EmptyState from '../components/EmptyState';

const PAGE_SIZE = 20;

const ATTRIBUTES = [
  { value: 'department', label: 'Department' },
  { value: 'job_title', label: 'Job Title' },
  { value: 'status', label: 'Status' },
  { value: 'email', label: 'Email' },
  { value: 'lifecycle_state', label: 'Lifecycle State' },
  { value: 'manager_name', label: 'Manager Name' },
  { value: 'source', label: 'Source' },
];

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does Not Contain' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_not_empty', label: 'Is Not Empty' },
];

export default function RolesPage() {
  const location = useLocation();
  const [roles, setRoles] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showAutoAssignDialog, setShowAutoAssignDialog] = useState(false);
  const [showDynamicDialog, setShowDynamicDialog] = useState(false);
  const [showMembershipDialog, setShowMembershipDialog] = useState(false);
  const [selectedRoleForRules, setSelectedRoleForRules] = useState(null);
  const [selectedRoleForDynamic, setSelectedRoleForDynamic] = useState(null);
  const [selectedRoleForMembership, setSelectedRoleForMembership] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', entitlements: [], requestable: true, privileged: false, dynamic: false, dynamic_rules: [], assignment_duration_days: '', renewal_reminder_days: 14, owner_id: '', owner_name: '', owner_email: '', approval_workflow: [] });
  const [editing, setEditing] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [autoAssignForm, setAutoAssignForm] = useState({ enabled: false, rules: [] });
  const [dynamicRules, setDynamicRules] = useState([]);
  const [dynamicMatchMode, setDynamicMatchMode] = useState('all');
  const [runningAutoAssign, setRunningAutoAssign] = useState(false);
  const [evaluatingDynamic, setEvaluatingDynamic] = useState(false);
  
  // Role Mining pre-fill: member identity IDs to assign after creation
  const [miningMemberIds, setMiningMemberIds] = useState([]);
  
  // Approval workflow states
  const [approvalStep, setApprovalStep] = useState({ type: 'manager', approver_id: '', approver_name: '' });
  const [approverSearch, setApproverSearch] = useState('');
  const [approverPopoverOpen, setApproverPopoverOpen] = useState(false);
  
  // Membership dialog states
  const [roleAssignments, setRoleAssignments] = useState([]);
  const [identities, setIdentities] = useState([]);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [newMemberForm, setNewMemberForm] = useState({ identity_id: '', expires_at: null });
  const [memberCalendarOpen, setMemberCalendarOpen] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberIdentitySearch, setMemberIdentitySearch] = useState('');
  const [memberIdentityPopoverOpen, setMemberIdentityPopoverOpen] = useState(false);
  
  // Entitlement search filter
  const [entitlementSearch, setEntitlementSearch] = useState('');
  
  // Wizard step for role create/edit
  const [wizardStep, setWizardStep] = useState(1);
  
  // Owner search
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerPopoverOpen, setOwnerPopoverOpen] = useState(false);
  
  // SoD protected roles
  const [sodProtectedRoles, setSodProtectedRoles] = useState(new Set());
  
  // Pagination and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [requestableFilter, setRequestableFilter] = useState('');
  const [dynamicFilter, setDynamicFilter] = useState('');

  // Filtered identities for owner selection
  const filteredOwnerIdentities = useMemo(() => {
    if (!ownerSearch) return identities.slice(0, 50); // Show first 50 if no search
    return identities.filter(i => 
      i.display_name?.toLowerCase().includes(ownerSearch.toLowerCase()) ||
      i.email?.toLowerCase().includes(ownerSearch.toLowerCase())
    ).slice(0, 50);
  }, [identities, ownerSearch]);

  const load = useCallback(async () => {
    try {
      const skip = (currentPage - 1) * PAGE_SIZE;
      const res = await API.get('/roles', { 
        params: { 
          search: search || undefined, 
          limit: PAGE_SIZE,
          skip: skip,
          sort_by: sortBy,
          sort_order: sortOrder,
          requestable: requestableFilter === 'true' ? true : requestableFilter === 'false' ? false : undefined,
          dynamic: dynamicFilter === 'true' ? true : dynamicFilter === 'false' ? false : undefined
        } 
      });
      setRoles(res.data.items);
      setTotal(res.data.total);
    } catch (err) { toast.error('Failed to load roles'); }
  }, [search, currentPage, sortBy, sortOrder, requestableFilter, dynamicFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy, sortOrder, requestableFilter, dynamicFilter]);

  useEffect(() => {
    API.get('/entitlements', { params: { limit: 200 } }).then(r => setEntitlements(r.data.items)).catch((e) => void 0);
    // Only load active (non-orphaned) identities for role assignments
    API.get('/identities', { params: { limit: 600, orphaned: false } }).then(r => {
      void 0;
      setIdentities(r.data.items || []);
    }).catch((e) => void 0);
    // Load SoD protected roles
    API.get('/sod/protected-roles').then(r => {
      setSodProtectedRoles(new Set(r.data.protected_role_ids || []));
    }).catch((e) => void 0);
  }, []);

  // Detect role mining navigation — open wizard pre-filled with suggestion data
  useEffect(() => {
    if (location.state?.fromMining && location.state?.suggestion) {
      const s = location.state.suggestion;
      setForm({
        name: s.name || '',
        description: s.description || '',
        entitlements: s.entitlements || [],
        requestable: true,
        privileged: false,
        dynamic: false,
        dynamic_rules: [],
        assignment_duration_days: '',
        renewal_reminder_days: 14,
        owner_id: '',
        owner_name: '',
        owner_email: '',
        approval_workflow: []
      });
      setMiningMemberIds(s.member_identity_ids || []);
      setEditing(null);
      setWizardStep(1);
      setShowDialog(true);
      // Clear location state so refreshing doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSort = (column, order) => {
    setSortBy(column);
    setSortOrder(order);
  };

  // Load role memberships
  const loadRoleMembers = async (roleId) => {
    setLoadingMembers(true);
    try {
      const res = await API.get('/role-assignments', { params: { role_id: roleId } });
      setRoleAssignments(res.data.items);
    } catch (err) { 
      toast.error('Failed to load members'); 
      setRoleAssignments([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Open membership dialog
  const openMembershipDialog = async (role) => {
    setSelectedRoleForMembership(role);
    setShowMembershipDialog(true);
    await loadRoleMembers(role.id);
  };

  // Add member to role
  const handleAddMember = async () => {
    if (!newMemberForm.identity_id || !selectedRoleForMembership) {
      toast.error('Please select an identity');
      return;
    }
    try {
      await API.post('/role-assignments', {
        identity_id: newMemberForm.identity_id,
        role_id: selectedRoleForMembership.id,
        assignment_type: 'manual',
        expires_at: newMemberForm.expires_at ? newMemberForm.expires_at.toISOString() : null
      });
      toast.success('Member added successfully');
      setShowAddMemberDialog(false);
      setNewMemberForm({ identity_id: '', expires_at: null });
      await loadRoleMembers(selectedRoleForMembership.id);
      load(); // Refresh role counts
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to add member'); 
    }
  };

  // Remove member from role
  const handleRemoveMember = async (assignmentId) => {
    if (!window.confirm('Remove this member from the role?')) return;
    try {
      await API.put(`/role-assignments/${assignmentId}/revoke`);
      toast.success('Member removed');
      await loadRoleMembers(selectedRoleForMembership.id);
      load(); // Refresh role counts
    } catch (err) { 
      toast.error('Failed to remove member'); 
    }
  };

  // Get identities not already in the role
  const getAvailableIdentities = () => {
    const assignedIds = new Set(roleAssignments.filter(a => a.status === 'active').map(a => a.identity_id));
    return identities.filter(i => !assignedIds.has(i.id));
  };

  const statusColor = (s) => {
    if (s === 'active') return 'bg-emerald-100 text-emerald-700';
    if (s === 'revoked') return 'bg-red-100 text-red-700';
    if (s === 'pending_renewal') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-navy-500';
  };

  const typeColor = (t) => {
    if (t === 'automatic') return 'bg-blue-100 text-blue-700';
    if (t === 'dynamic') return 'bg-purple-100 text-purple-700';
    if (t === 'request') return 'bg-cyan-100 text-cyan-700';
    return 'bg-slate-100 text-navy-600';
  };

  const handleSave = async () => {
    try {
      const payload = { ...form };
      // Convert empty assignment_duration_days to null
      if (payload.assignment_duration_days === '' || payload.assignment_duration_days === 0) {
        payload.assignment_duration_days = null;
      }
      // Convert empty owner_id to null
      if (payload.owner_id === '') {
        payload.owner_id = null;
        payload.owner_name = null;
        payload.owner_email = null;
      }
      if (editing) {
        await API.put(`/roles/${editing}`, payload);
        toast.success('Role updated');
      } else {
        const res = await API.post('/roles', payload);
        const newRoleId = res.data?.id;
        toast.success('Role created');
        // If from role mining, bulk-assign members to the new role
        if (miningMemberIds.length > 0 && newRoleId) {
          try {
            const bulkRes = await API.post('/role-assignments/bulk-mining', {
              role_id: newRoleId,
              identity_ids: miningMemberIds
            });
            const { assigned, skipped } = bulkRes.data;
            if (assigned > 0) {
              toast.success(`${assigned} member${assigned !== 1 ? 's' : ''} assigned from role mining suggestion${skipped > 0 ? ` (${skipped} skipped)` : ''}`);
            }
          } catch (err) {
            console.error('Bulk member assignment failed:', err?.response?.data?.detail || err.message);
            toast.error('Role created but member assignment failed');
          }
          setMiningMemberIds([]);
        }
      }
      setShowDialog(false);
      setForm({ name: '', description: '', entitlements: [], requestable: true, privileged: false, dynamic: false, dynamic_rules: [], assignment_duration_days: '', renewal_reminder_days: 14, owner_id: '', owner_name: '', owner_email: '', approval_workflow: [] });
      setEntitlementSearch('');
      setApprovalStep({ type: 'manager', approver_id: '', approver_name: '' });
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleEdit = (role) => {
    setForm({ 
      name: role.name, 
      description: role.description || '', 
      entitlements: role.entitlements || [], 
      requestable: role.requestable ?? true,
      privileged: role.privileged ?? false,
      dynamic: role.dynamic ?? false,
      dynamic_rules: role.dynamic_rules || [],
      assignment_duration_days: role.assignment_duration_days || '',
      renewal_reminder_days: role.renewal_reminder_days || 14,
      owner_id: role.owner_id || '',
      owner_name: role.owner_name || '',
      owner_email: role.owner_email || '',
      approval_workflow: role.approval_workflow || []
    });
    setEntitlementSearch('');
    setOwnerSearch('');
    setApprovalStep({ type: 'manager', approver_id: '', approver_name: '' });
    setEditing(role.id);
    setWizardStep(1);
    setShowDialog(true);
  };

  const addApprovalStep = () => {
    if (approvalStep.type === 'specific_identity' && !approvalStep.approver_id) {
      toast.error('Please select an approver for specific identity type');
      return;
    }
    const newStep = {
      order: form.approval_workflow.length + 1,
      type: approvalStep.type,
      approver_id: approvalStep.type === 'specific_identity' ? approvalStep.approver_id : null,
      approver_name: approvalStep.type === 'specific_identity' ? approvalStep.approver_name : null
    };
    setForm(prev => ({
      ...prev,
      approval_workflow: [...prev.approval_workflow, newStep]
    }));
    setApprovalStep({ type: 'manager', approver_id: '', approver_name: '' });
    setApproverSearch('');
  };

  const removeApprovalStep = (index) => {
    setForm(prev => ({
      ...prev,
      approval_workflow: prev.approval_workflow.filter((_, i) => i !== index).map((step, i) => ({ ...step, order: i + 1 }))
    }));
  };

  const getApproverTypeLabel = (type) => {
    switch(type) {
      case 'manager': return "Requester's Manager";
      case 'role_owner': return 'Role Owner';
      case 'specific_identity': return 'Specific Approver';
      default: return type;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this role?')) return;
    try { await API.delete(`/roles/${id}`); toast.success('Role deleted'); load(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const toggleEntitlement = (entId) => {
    setForm(prev => ({
      ...prev,
      entitlements: prev.entitlements.includes(entId)
        ? prev.entitlements.filter(e => e !== entId)
        : [...prev.entitlements, entId]
    }));
  };

  const openAutoAssignDialog = (role) => {
    setSelectedRoleForRules(role);
    const rules = role.auto_assign_rules || {};
    setAutoAssignForm({
      enabled: rules.enabled || false,
      rules: rules.rules && rules.rules.length > 0
        ? rules.rules
        : [{ department: '', job_title: '', match_type: 'contains' }]
    });
    setShowAutoAssignDialog(true);
  };

  const addRule = () => {
    setAutoAssignForm(prev => ({
      ...prev,
      rules: [...prev.rules, { department: '', job_title: '', match_type: 'contains' }]
    }));
  };

  const updateRule = (idx, field, value) => {
    setAutoAssignForm(prev => ({
      ...prev,
      rules: prev.rules.map((r, i) => i === idx ? { ...r, [field]: value } : r)
    }));
  };

  const removeRule = (idx) => {
    setAutoAssignForm(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== idx)
    }));
  };

  const saveAutoAssignRules = async () => {
    if (!selectedRoleForRules) return;
    try {
      await API.put(`/roles/${selectedRoleForRules.id}/auto-assign-rules`, autoAssignForm);
      toast.success('Auto-assign rules saved');
      setShowAutoAssignDialog(false);
      load();
    } catch (err) { toast.error('Failed to save rules'); }
  };

  const handleRunAutoAssign = async () => {
    setRunningAutoAssign(true);
    try {
      const res = await API.post('/roles/run-auto-assign');
      toast.success(res.data.message);
    } catch (err) { toast.error('Failed to run auto-assign'); }
    finally { setRunningAutoAssign(false); }
  };

  const handleEvaluateDynamic = async () => {
    setEvaluatingDynamic(true);
    try {
      const res = await API.post('/roles/evaluate-dynamic');
      toast.success(`${res.data.message}: ${res.data.added} added, ${res.data.removed} removed`);
      load();
    } catch (err) { toast.error('Failed to evaluate dynamic roles'); }
    finally { setEvaluatingDynamic(false); }
  };

  const handleEvaluateSingleRole = async (roleId) => {
    try {
      const res = await API.post(`/roles/${roleId}/evaluate`);
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to evaluate'); }
  };

  const hasAutoAssign = (role) => {
    return role.auto_assign_rules?.enabled && (role.auto_assign_rules?.rules || []).length > 0;
  };

  const openDynamicDialog = (role) => {
    setSelectedRoleForDynamic(role);
    setDynamicRules(role.dynamic_rules || [{ attribute: 'department', operator: 'equals', value: '' }]);
    setDynamicMatchMode(role.dynamic_match_mode || 'all');
    setShowDynamicDialog(true);
  };

  const addDynamicRule = () => {
    setDynamicRules(prev => [...prev, { attribute: 'department', operator: 'equals', value: '' }]);
  };

  const updateDynamicRule = (idx, field, value) => {
    setDynamicRules(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const removeDynamicRule = (idx) => {
    setDynamicRules(prev => prev.filter((_, i) => i !== idx));
  };

  const saveDynamicRules = async () => {
    if (!selectedRoleForDynamic) return;
    try {
      await API.put(`/roles/${selectedRoleForDynamic.id}`, {
        dynamic: true,
        dynamic_rules: dynamicRules,
        dynamic_match_mode: dynamicMatchMode
      });
      toast.success('Dynamic rules saved');
      setShowDynamicDialog(false);
      load();
    } catch (err) { toast.error('Failed to save rules'); }
  };

  const disableDynamic = async () => {
    if (!selectedRoleForDynamic) return;
    try {
      await API.put(`/roles/${selectedRoleForDynamic.id}`, { dynamic: false, dynamic_rules: [] });
      toast.success('Dynamic role disabled');
      setShowDynamicDialog(false);
      load();
    } catch (err) { toast.error('Failed to disable'); }
  };

  return (
    <div data-testid="roles-page" className="p-4 sm:p-6 md:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-navy-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Roles</h1>
          <p className="text-sm text-navy-500 mt-1">{total} roles configured. Bundle entitlements into roles.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={handleEvaluateDynamic} disabled={evaluatingDynamic} className="rounded-sm text-sm" data-testid="evaluate-dynamic-button">
            <ArrowsClockwise size={14} className={`mr-1.5 ${evaluatingDynamic ? 'animate-spin' : ''}`} />
            <span className="hidden lg:inline">{evaluatingDynamic ? 'Evaluating...' : 'Evaluate Dynamic'}</span>
            <span className="lg:hidden">{evaluatingDynamic ? '...' : 'Evaluate'}</span>
          </Button>
          <Button variant="outline" onClick={handleRunAutoAssign} disabled={runningAutoAssign} className="rounded-sm text-sm" data-testid="run-auto-assign-button">
            <Play size={14} className={`mr-1.5 ${runningAutoAssign ? 'animate-spin' : ''}`} />
            <span className="hidden lg:inline">{runningAutoAssign ? 'Running...' : 'Auto-Assign'}</span>
            <span className="lg:hidden">{runningAutoAssign ? '...' : 'Assign'}</span>
          </Button>
          <Button onClick={() => { setForm({ name: '', description: '', entitlements: [], requestable: true, privileged: false, dynamic: false, dynamic_rules: [], assignment_duration_days: '', renewal_reminder_days: 14, owner_id: '', owner_name: '', owner_email: '', approval_workflow: [] }); setEntitlementSearch(''); setOwnerSearch(''); setApprovalStep({ type: 'manager', approver_id: '', approver_name: '' }); setEditing(null); setWizardStep(1); setMiningMemberIds([]); setShowDialog(true); }} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm shadow-sm" data-testid="create-role-button">
            <Plus size={16} className="mr-1.5" /> New Role
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
          <Input placeholder="Search roles..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 rounded-sm border-slate-200" data-testid="role-search-input" />
        </div>
        <Select value={requestableFilter || 'all'} onValueChange={(v) => setRequestableFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 h-9 rounded-sm text-xs" data-testid="role-requestable-filter">
            <SelectValue placeholder="Requestable" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Requestable</SelectItem>
            <SelectItem value="false">Not Requestable</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dynamicFilter || 'all'} onValueChange={(v) => setDynamicFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32 h-9 rounded-sm text-xs" data-testid="role-dynamic-filter">
            <SelectValue placeholder="Dynamic" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Dynamic</SelectItem>
            <SelectItem value="false">Static</SelectItem>
          </SelectContent>
        </Select>
        {(requestableFilter || dynamicFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setRequestableFilter(''); setDynamicFilter(''); }} className="text-xs text-navy-500 h-9">Clear filters</Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="h-1.5 bg-navy-600" />
        <Table wrapperClassName="max-h-[calc(100vh-340px)]">
          <TableHeader>
            <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
              <SortableTableHeader column="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Role Name</SortableTableHeader>
              <SortableTableHeader column="description" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Description</SortableTableHeader>
              <SortableTableHeader column="owner_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Owner</SortableTableHeader>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Entitlements</TableHead>
              <SortableTableHeader column="assignment_count" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Members</SortableTableHeader>
              <SortableTableHeader column="assignment_duration_days" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Assignment Duration</SortableTableHeader>
              <SortableTableHeader column="dynamic" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Dynamic</SortableTableHeader>
              <SortableTableHeader column="requestable" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Requestable</SortableTableHeader>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.length === 0 ? (
              <TableRow><TableCell colSpan={9}><EmptyState type="roles" onAction={() => { setForm({ name: '', description: '', entitlements: [], requestable: true, privileged: false, dynamic: false, dynamic_rules: [], assignment_duration_days: '', renewal_reminder_days: 14, owner_id: '', owner_name: '', owner_email: '', approval_workflow: [] }); setEditing(null); setWizardStep(1); setShowDialog(true); }} /></TableCell></TableRow>
            ) : roles.map((role) => (
              <TableRow key={role.id} className="hover:bg-slate-50 transition-colors" data-testid={`role-row-${role.id}`}>
                <TableCell className="font-medium text-sm text-navy-900">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={14} className={role.is_incomplete ? 'text-slate-400' : 'text-orange-500'} />
                    <span className={role.is_incomplete ? 'text-navy-400' : ''}>{role.name}</span>
                    {role.is_incomplete && (
                      <Badge className="text-[9px] bg-red-100 text-red-600" data-testid={`incomplete-badge-${role.id}`}>
                        Incomplete
                      </Badge>
                    )}
                    {sodProtectedRoles.has(role.id) && (
                      <div title="This role is part of a Separation of Duties policy">
                        <Lock size={12} className="text-amber-500" />
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-navy-600 max-w-xs truncate">{role.description || '-'}</TableCell>
                <TableCell className="text-sm text-navy-600">{role.owner_name || <span className="text-navy-400">-</span>}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Key size={12} className={(role.entitlements || []).length === 0 ? 'text-red-400' : 'text-navy-400'} />
                    <span className={`text-sm ${(role.entitlements || []).length === 0 ? 'text-red-500 font-semibold' : 'text-navy-600'}`}>{(role.entitlements || []).length}</span>
                    {(role.entitlements || []).length === 0 && <span className="text-[9px] text-red-400">Required</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => openMembershipDialog(role)} 
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 gap-1.5 h-7 px-2"
                    data-testid={`members-btn-${role.id}`}
                  >
                    <Users size={14} />
                    <span className="font-semibold">{role.assignment_count || 0}</span>
                  </Button>
                </TableCell>
                <TableCell className="text-sm text-navy-600">
                  {role.assignment_duration_days ? (
                    <div className="flex items-center gap-1">
                      <Calendar size={12} className="text-amber-500" />
                      <span>{role.assignment_duration_days} days</span>
                    </div>
                  ) : (
                    <span className="text-navy-400">No limit</span>
                  )}
                </TableCell>
                <TableCell>
                  {role.dynamic ? (
                    <Badge className="text-[10px] bg-purple-100 text-purple-700 cursor-pointer" onClick={() => openDynamicDialog(role)}>
                      <Lightning size={10} className="mr-0.5" /> {(role.dynamic_rules || []).length} rules
                    </Badge>
                  ) : (
                    <button onClick={() => openDynamicDialog(role)} className="text-[10px] text-navy-400 hover:text-purple-500 transition-colors" data-testid={`dynamic-btn-${role.id}`}>
                      Configure
                    </button>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Badge variant={role.requestable ? 'default' : 'secondary'} className={`text-[10px] ${role.requestable && !role.is_incomplete ? 'bg-emerald-100 text-emerald-700' : role.requestable && role.is_incomplete ? 'bg-amber-100 text-amber-700' : ''}`}>
                      {role.requestable ? (role.is_incomplete ? 'Blocked' : 'Yes') : 'No'}
                    </Badge>
                    {role.privileged && (
                      <Badge className="text-[10px] bg-amber-100 text-amber-700">Privileged</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {role.dynamic && (
                      <Button variant="ghost" size="sm" onClick={() => handleEvaluateSingleRole(role.id)} className="text-purple-500 hover:text-purple-700" title="Evaluate this role" data-testid={`evaluate-role-${role.id}`}>
                        <Play size={14} />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(role)} data-testid={`edit-role-${role.id}`}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(role.id)} className="text-red-500 hover:text-red-700"><Trash size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {total > 0 && (
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm shadow-sm px-4 py-3">
          <div className="text-sm text-navy-500">
            Showing <span className="font-medium text-navy-700">{((currentPage - 1) * PAGE_SIZE) + 1}</span> to{' '}
            <span className="font-medium text-navy-700">{Math.min(currentPage * PAGE_SIZE, total)}</span> of{' '}
            <span className="font-medium text-navy-700">{total}</span> roles
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-sm h-8 px-3"
            >
              <CaretLeft size={14} className="mr-1" /> Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`rounded-sm h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="rounded-sm h-8 px-3"
            >
              Next <CaretRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) setMiningMemberIds([]); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{editing ? 'Edit Role' : miningMemberIds.length > 0 ? 'Create Role from Mining Suggestion' : 'Create Role'}</DialogTitle>
            {/* Role Mining Banner */}
            {!editing && miningMemberIds.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-sm p-3 mt-2" data-testid="mining-prefill-banner">
                <p className="text-xs text-purple-700">
                  <span className="font-semibold">Pre-filled from Role Mining:</span> {form.entitlements.length} entitlements and {miningMemberIds.length} members will be auto-assigned on save. Complete the role configuration below.
                </p>
              </div>
            )}
            {/* Step Indicator */}
            <div className="flex items-center gap-2 pt-3" data-testid="wizard-steps">
              {[
                { num: 1, label: 'Basics' },
                { num: 2, label: 'Access' },
                { num: 3, label: 'Automation' },
              ].map((s, i) => (
                <div key={s.num} className="flex items-center gap-2 flex-1">
                  <button
                    data-testid={`wizard-step-${s.num}`}
                    onClick={() => setWizardStep(s.num)}
                    className={`flex items-center gap-2 w-full px-3 py-2 rounded-md text-xs font-semibold transition-all ${
                      wizardStep === s.num
                        ? 'bg-orange-50 text-orange-700 border border-orange-200'
                        : wizardStep > s.num
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-slate-50 text-navy-400 border border-slate-200'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      wizardStep === s.num
                        ? 'bg-orange-500 text-white'
                        : wizardStep > s.num
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-200 text-navy-500'
                    }`}>
                      {wizardStep > s.num ? <Check size={10} weight="bold" /> : s.num}
                    </span>
                    <span className="hidden sm:inline">{s.label}</span>
                  </button>
                  {i < 2 && <div className={`w-4 h-px shrink-0 ${wizardStep > s.num ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
                </div>
              ))}
            </div>
          </DialogHeader>
          <div className="py-2 min-h-[300px]">
            {/* STEP 1: Basics */}
            {wizardStep === 1 && (
              <div className="space-y-4" data-testid="wizard-step-1-content">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest">Role Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="role-name-input" placeholder="e.g., Engineering Access" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest">Role Owner</Label>
                <Popover open={ownerPopoverOpen} onOpenChange={setOwnerPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={ownerPopoverOpen}
                      className="mt-1 w-full justify-between rounded-sm h-10 font-normal"
                      data-testid="role-owner-select"
                    >
                      {form.owner_name ? (
                        <span className="flex items-center gap-2 min-w-0">
                          <User size={14} className="text-navy-400 shrink-0" />
                          <span className="truncate">{form.owner_name}</span>
                          {form.owner_email && <span className="text-xs text-navy-400 truncate">({form.owner_email})</span>}
                        </span>
                      ) : (
                        <span className="text-navy-400">Select an owner...</span>
                      )}
                      <CaretUpDown size={14} className="text-navy-400" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <div className="p-2 border-b border-slate-200">
                      <div className="relative">
                        <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-navy-400" />
                        <Input
                          placeholder="Search identities..."
                          value={ownerSearch}
                          onChange={(e) => setOwnerSearch(e.target.value)}
                          className="pl-7 h-8 text-sm rounded-sm"
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <div
                        className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-100"
                        onClick={() => {
                          setForm({...form, owner_id: '', owner_name: '', owner_email: ''});
                          setOwnerPopoverOpen(false);
                          setOwnerSearch('');
                        }}
                      >
                        <div className={`w-4 h-4 flex items-center justify-center ${!form.owner_id ? 'text-orange-500' : 'text-transparent'}`}>
                          <Check size={14} />
                        </div>
                        <span className="text-sm text-navy-500">No owner</span>
                      </div>
                      {filteredOwnerIdentities.length === 0 ? (
                        <div className="px-3 py-4 text-center text-sm text-navy-400">
                          No identities found
                        </div>
                      ) : filteredOwnerIdentities.map(identity => (
                        <div
                          key={identity.id}
                          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-100"
                          onClick={() => {
                            setForm({...form, owner_id: identity.id, owner_name: identity.display_name, owner_email: identity.email || ''});
                            setOwnerPopoverOpen(false);
                            setOwnerSearch('');
                          }}
                        >
                          <div className={`w-4 h-4 flex items-center justify-center ${form.owner_id === identity.id ? 'text-orange-500' : 'text-transparent'}`}>
                            <Check size={14} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-navy-700 truncate">{identity.display_name}</p>
                            {identity.email && <p className="text-xs text-navy-400 truncate">{identity.email}</p>}
                          </div>
                        </div>
                      ))}
                      {identities.length > 50 && !ownerSearch && (
                        <div className="px-3 py-2 text-xs text-navy-400 text-center border-t border-slate-100">
                          Type to search {identities.length} identities
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 rounded-sm" placeholder="What does this role grant access to?" />
            </div>
              </div>
            )}

            {/* STEP 2: Access */}
            {wizardStep === 2 && (
              <div className="space-y-4" data-testid="wizard-step-2-content">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Switch checked={form.requestable} onCheckedChange={(val) => setForm({...form, requestable: val})} disabled={form.privileged} />
                  <Label className={`text-sm ${form.privileged ? 'text-navy-400' : ''}`}>Requestable in Access Portal</Label>
                  <InfoTooltip concept="requestable" />
                </div>
                {form.privileged && (
                  <p className="text-[10px] text-amber-600 -mt-1 ml-12">Privileged roles cannot be directly requested. Use JIT Access instead.</p>
                )}
                <div className="flex items-center gap-3">
                  <Switch checked={form.privileged} onCheckedChange={(val) => setForm({...form, privileged: val, ...(val ? { requestable: false } : {})})} data-testid="role-privileged-toggle" />
                  <Label className="text-sm">Privileged Role (JIT Access)</Label>
                  <InfoTooltip concept="privileged" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-widest">Assignment Duration (Days)</Label>
                    <InfoTooltip concept="assignment_duration" />
                <p className="text-xs text-navy-400 mt-0.5 mb-1">How long role assignments will last. Leave empty for no limit.</p>
                <div className="flex items-center gap-2 mt-1">
                  <Calendar size={16} className="text-navy-400" />
                  <Input 
                    type="number" 
                    placeholder="e.g., 90, 180, 365"
                    value={form.assignment_duration_days} 
                    onChange={(e) => setForm({...form, assignment_duration_days: e.target.value ? parseInt(e.target.value) : ''})} 
                    className="rounded-sm w-32" 
                    data-testid="role-duration-input"
                    min={1}
                  />
                  <span className="text-sm text-navy-500">days</span>
                </div>
                {form.assignment_duration_days && (
                  <p className="text-xs text-amber-600 mt-1">
                    Users will be notified {form.renewal_reminder_days || 14} days before expiry to renew
                  </p>
                )}
              </div>
            </div>
            
            {/* Dual-list entitlement selector */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest mb-2 block">Bundle Entitlements <InfoTooltip concept="entitlements" /></Label>
              {entitlements.length === 0 ? (
                <p className="text-sm text-navy-400">No entitlements available. Create entitlements first.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {/* Available Entitlements */}
                  <div className="border border-slate-200 rounded-sm">
                    <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
                      <p className="text-xs font-semibold text-navy-600 uppercase">Available ({entitlements.filter(e => !form.entitlements.includes(e.id)).length})</p>
                      <div className="relative mt-1">
                        <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-navy-400" />
                        <Input 
                          placeholder="Search entitlements..." 
                          value={entitlementSearch} 
                          onChange={(e) => setEntitlementSearch(e.target.value)} 
                          className="pl-7 h-8 text-sm rounded-sm" 
                        />
                      </div>
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {entitlements
                        .filter(e => !form.entitlements.includes(e.id))
                        .filter(e => entitlementSearch === '' || e.name.toLowerCase().includes(entitlementSearch.toLowerCase()))
                        .map(ent => (
                          <div 
                            key={ent.id} 
                            onClick={() => toggleEntitlement(ent.id)}
                            className="flex items-center justify-between px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0 group"
                          >
                            <div>
                              <p className="text-sm font-medium text-navy-700">{ent.name}</p>
                              <p className="text-xs text-navy-400">{ent.type}</p>
                            </div>
                            <CaretRight size={14} className="text-navy-300 group-hover:text-blue-500" />
                          </div>
                        ))}
                      {entitlements.filter(e => !form.entitlements.includes(e.id)).filter(e => entitlementSearch === '' || e.name.toLowerCase().includes(entitlementSearch.toLowerCase())).length === 0 && (
                        <p className="text-xs text-navy-400 text-center py-4">No entitlements found</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Selected Entitlements */}
                  <div className="border border-emerald-200 rounded-sm bg-emerald-50/30">
                    <div className="bg-emerald-50 px-3 py-2 border-b border-emerald-200">
                      <p className="text-xs font-semibold text-emerald-700 uppercase">Selected ({form.entitlements.length})</p>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {form.entitlements.length === 0 ? (
                        <p className="text-xs text-navy-400 text-center py-4">Click entitlements to add</p>
                      ) : form.entitlements.map(entId => {
                        const ent = entitlements.find(e => e.id === entId);
                        if (!ent) return null;
                        return (
                          <div 
                            key={ent.id} 
                            onClick={() => toggleEntitlement(ent.id)}
                            className="flex items-center justify-between px-3 py-2 hover:bg-red-50 cursor-pointer border-b border-emerald-100 last:border-0 group"
                          >
                            <div className="flex items-center gap-2">
                              <Key size={12} className="text-emerald-500" />
                              <div>
                                <p className="text-sm font-medium text-navy-700">{ent.name}</p>
                                <p className="text-xs text-navy-400">{ent.type}</p>
                              </div>
                            </div>
                            <XCircle size={14} className="text-navy-300 group-hover:text-red-500" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
              </div>
            )}
            
            {/* STEP 3: Automation */}
            {wizardStep === 3 && (
              <div className="space-y-5" data-testid="wizard-step-3-content">
            {/* Approval Workflow Section */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest mb-3 block flex items-center gap-2">
                <Lock size={14} className="text-navy-500" />
                Approval Workflow
                <InfoTooltip concept="approval_workflow" />
              </Label>
              <p className="text-xs text-navy-500 mb-3">
                Define who must approve access requests. Multiple approvers create a sequential chain.
              </p>
              
              {/* Add Approval Step */}
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-3">
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs text-navy-500 mb-1 block">Approver Type</Label>
                    <Select value={approvalStep.type} onValueChange={(v) => setApprovalStep({...approvalStep, type: v, approver_id: '', approver_name: ''})}>
                      <SelectTrigger className="rounded-sm" data-testid="approval-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manager">Requester's Manager</SelectItem>
                        <SelectItem value="role_owner">Role Owner</SelectItem>
                        <SelectItem value="specific_identity">Specific Approver</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {approvalStep.type === 'specific_identity' && (
                    <div className="flex-1">
                      <Label className="text-xs text-navy-500 mb-1 block">Select Approver</Label>
                      <Popover open={approverPopoverOpen} onOpenChange={setApproverPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start rounded-sm text-left font-normal" data-testid="approver-select-trigger">
                            {approvalStep.approver_name || 'Select approver...'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[280px] p-0" align="start">
                          <div className="p-2 border-b">
                            <Input
                              placeholder="Search identities..."
                              value={approverSearch}
                              onChange={(e) => setApproverSearch(e.target.value)}
                              className="rounded-sm"
                            />
                          </div>
                          <div className="max-h-[200px] overflow-y-auto">
                            {identities
                              .filter(i => approverSearch === '' || i.display_name?.toLowerCase().includes(approverSearch.toLowerCase()) || i.email?.toLowerCase().includes(approverSearch.toLowerCase()))
                              .slice(0, 50)
                              .map(identity => (
                                <div
                                  key={identity.id}
                                  onClick={() => {
                                    setApprovalStep({...approvalStep, approver_id: identity.id, approver_name: identity.display_name});
                                    setApproverPopoverOpen(false);
                                    setApproverSearch('');
                                  }}
                                  className="px-3 py-2 hover:bg-slate-100 cursor-pointer"
                                >
                                  <p className="text-sm font-medium">{identity.display_name}</p>
                                  {identity.email && <p className="text-xs text-navy-400">{identity.email}</p>}
                                </div>
                              ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                  
                  <Button onClick={addApprovalStep} variant="outline" size="sm" className="rounded-sm" data-testid="add-approval-step-btn">
                    <Plus size={14} className="mr-1" /> Add Step
                  </Button>
                </div>
              </div>
              
              {/* Approval Steps List */}
              {form.approval_workflow.length > 0 ? (
                <div className="space-y-2">
                  {form.approval_workflow.map((step, index) => (
                    <div key={`step-${step.order}-${step.type}`} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2">
                      <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {step.order}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-emerald-900">{getApproverTypeLabel(step.type)}</p>
                        {step.type === 'specific_identity' && step.approver_name && (
                          <p className="text-xs text-emerald-700">{step.approver_name}</p>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeApprovalStep(index)} className="text-red-500 hover:text-red-700 h-6 w-6 p-0" data-testid={`remove-approval-step-${index}`}>
                        <Trash size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 bg-slate-50 rounded-sm border border-slate-200">
                  <p className="text-xs text-navy-400">No approval steps defined</p>
                  <p className="text-xs text-navy-400">Requests will go to any admin for approval</p>
                </div>
              )}
            </div>

            {/* Dynamic Role Toggle */}
            <div className="border-t border-slate-200 pt-4">
              <div className="flex items-center gap-3">
                <Switch checked={form.dynamic} onCheckedChange={(val) => setForm({...form, dynamic: val})} data-testid="role-dynamic-toggle" />
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">Dynamic Role <InfoTooltip concept="dynamic_role" /></Label>
                  <p className="text-xs text-navy-400">Auto-assign based on identity attributes. Configure rules after saving.</p>
                </div>
              </div>
            </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <div>
              {wizardStep > 1 && (
                <Button variant="outline" onClick={() => setWizardStep(wizardStep - 1)} className="rounded-sm" data-testid="wizard-back-btn">
                  <CaretLeft size={14} className="mr-1" /> Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm">Cancel</Button>
              {wizardStep < 3 ? (
                <Button onClick={() => {
                  if (wizardStep === 1 && !form.name.trim()) {
                    toast.error('Role name is required');
                    return;
                  }
                  setWizardStep(wizardStep + 1);
                }} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="wizard-next-btn">
                  Next <CaretRight size={14} className="ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSave} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-role-button">
                  <CheckCircle size={14} className="mr-1" /> {editing ? 'Update Role' : 'Create Role'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AUTO-ASSIGN RULES DIALOG */}
      <Dialog open={showAutoAssignDialog} onOpenChange={setShowAutoAssignDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              <div className="flex items-center gap-2">
                <Lightning size={18} className="text-orange-500" />
                Auto-Assign Rules: {selectedRoleForRules?.name}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3">
              <Switch checked={autoAssignForm.enabled} onCheckedChange={(val) => setAutoAssignForm({...autoAssignForm, enabled: val})} />
              <Label className="text-sm font-medium">Enable Auto-Assignment</Label>
            </div>

            <p className="text-xs text-navy-500">
              When enabled, identities matching ANY of the rules below will be automatically assigned this role when "Run Auto-Assign" is triggered.
            </p>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-widest">Rules</Label>
                <Button variant="outline" size="sm" onClick={addRule} className="rounded-sm text-xs" data-testid="add-auto-assign-rule">
                  <Plus size={12} className="mr-1" /> Add Rule
                </Button>
              </div>

              {autoAssignForm.rules.map((rule, idx) => (
                <div key={rule.id || `rule-${idx}`} className="border border-slate-200 rounded-sm p-3 space-y-3 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-navy-400 uppercase tracking-widest">Rule {idx + 1}</span>
                    {autoAssignForm.rules.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeRule(idx)} className="text-red-500 hover:text-red-700 h-6 w-6 p-0">
                        <Trash size={12} />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] text-navy-500 uppercase tracking-widest">Department</Label>
                      <Input value={rule.department || ''} onChange={(e) => updateRule(idx, 'department', e.target.value)}
                        className="mt-1 rounded-sm text-sm h-8" placeholder="e.g., Engineering"
                        data-testid={`rule-department-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] text-navy-500 uppercase tracking-widest">Job Title</Label>
                      <Input value={rule.job_title || ''} onChange={(e) => updateRule(idx, 'job_title', e.target.value)}
                        className="mt-1 rounded-sm text-sm h-8" placeholder="e.g., Developer"
                        data-testid={`rule-job-title-${idx}`} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-navy-500 uppercase tracking-widest">Match Type</Label>
                    <Select value={rule.match_type || 'contains'} onValueChange={(v) => updateRule(idx, 'match_type', v)}>
                      <SelectTrigger className="mt-1 rounded-sm h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contains">Contains (partial match)</SelectItem>
                        <SelectItem value="exact">Exact Match</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAutoAssignDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveAutoAssignRules} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-auto-assign-rules-button">
              Save Rules
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DYNAMIC ROLE RULES DIALOG */}
      <Dialog open={showDynamicDialog} onOpenChange={setShowDynamicDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              <div className="flex items-center gap-2">
                <Lightning size={18} className="text-purple-500" />
                Dynamic Role Rules: {selectedRoleForDynamic?.name}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-purple-50 border border-purple-200 rounded-sm p-4">
              <div className="flex items-start gap-3">
                <WarningCircle size={18} className="text-purple-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-purple-900">Dynamic Role Assignment</p>
                  <p className="text-xs text-purple-700 mt-1">
                    Identities matching these rules will be <strong>automatically assigned</strong> this role. 
                    When an identity's attributes change and no longer match, they will be <strong>automatically removed</strong> from the role.
                    Changes are synced to Azure AD groups.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Label className="text-xs font-semibold uppercase tracking-widest">Match Mode</Label>
              <Select value={dynamicMatchMode} onValueChange={setDynamicMatchMode}>
                <SelectTrigger className="w-48 rounded-sm h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ALL rules must match (AND)</SelectItem>
                  <SelectItem value="any">ANY rule matches (OR)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-widest">Rules</Label>
                <Button variant="outline" size="sm" onClick={addDynamicRule} className="rounded-sm text-xs" data-testid="add-dynamic-rule">
                  <Plus size={12} className="mr-1" /> Add Rule
                </Button>
              </div>

              {dynamicRules.length === 0 && (
                <div className="text-center py-8 text-sm text-navy-400 border border-dashed border-slate-200 rounded-sm">
                  <Lightning size={24} className="mx-auto mb-2 text-navy-300" />
                  No rules configured. Add a rule to enable dynamic assignment.
                </div>
              )}

              {dynamicRules.map((rule, idx) => (
                <div key={`ent-${idx}`} className="border border-slate-200 rounded-sm p-4 space-y-3 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-navy-400 uppercase tracking-widest">Rule {idx + 1}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeDynamicRule(idx)} className="text-red-500 hover:text-red-700 h-6 w-6 p-0">
                      <Trash size={12} />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[10px] text-navy-500 uppercase tracking-widest">Attribute</Label>
                      <Select value={rule.attribute || ''} onValueChange={(v) => updateDynamicRule(idx, 'attribute', v)}>
                        <SelectTrigger className="mt-1 rounded-sm h-9 text-sm" data-testid={`dynamic-attribute-${idx}`}>
                          <SelectValue placeholder="Select attribute" />
                        </SelectTrigger>
                        <SelectContent>
                          {ATTRIBUTES.map(attr => (
                            <SelectItem key={attr.value} value={attr.value}>{attr.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-navy-500 uppercase tracking-widest">Operator</Label>
                      <Select value={rule.operator || 'equals'} onValueChange={(v) => updateDynamicRule(idx, 'operator', v)}>
                        <SelectTrigger className="mt-1 rounded-sm h-9 text-sm" data-testid={`dynamic-operator-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OPERATORS.map(op => (
                            <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] text-navy-500 uppercase tracking-widest">Value</Label>
                      {rule.operator === 'is_empty' || rule.operator === 'is_not_empty' ? (
                        <Input disabled value="N/A" className="mt-1 rounded-sm h-9 text-sm bg-slate-100" />
                      ) : (
                        <Input 
                          value={rule.value || ''} 
                          onChange={(e) => updateDynamicRule(idx, 'value', e.target.value)}
                          className="mt-1 rounded-sm h-9 text-sm" 
                          placeholder="e.g., Engineering"
                          data-testid={`dynamic-value-${idx}`}
                        />
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-navy-500 bg-white rounded px-2 py-1 border border-slate-100">
                    <span className="font-medium">Preview:</span> If <code className="bg-slate-100 px-1 rounded">{rule.attribute || '?'}</code> {' '}
                    <code className="bg-purple-100 text-purple-700 px-1 rounded">{OPERATORS.find(o => o.value === rule.operator)?.label || '?'}</code> {' '}
                    {rule.operator !== 'is_empty' && rule.operator !== 'is_not_empty' && (
                      <><code className="bg-emerald-100 text-emerald-700 px-1 rounded">"{rule.value || '?'}"</code></>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="flex items-center justify-between gap-2">
            <Button variant="outline" onClick={disableDynamic} className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="disable-dynamic-button">
              Disable Dynamic
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowDynamicDialog(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={saveDynamicRules} className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm" data-testid="save-dynamic-rules-button">
                Save & Enable
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MEMBERSHIP DIALOG */}
      <Dialog open={showMembershipDialog} onOpenChange={setShowMembershipDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} className="text-orange-500" />
                {selectedRoleForMembership?.name} — Members
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="active" className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-3">
              <TabsList className="bg-slate-100 rounded-sm p-0.5 h-auto">
                <TabsTrigger value="active" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2">
                  Active ({roleAssignments.filter(a => a.status === 'active').length})
                </TabsTrigger>
                <TabsTrigger value="all" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2">
                  All ({roleAssignments.length})
                </TabsTrigger>
              </TabsList>
              <Button 
                onClick={() => setShowAddMemberDialog(true)} 
                disabled={selectedRoleForMembership?.is_incomplete}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-sm h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-member-button"
                title={selectedRoleForMembership?.is_incomplete ? 'Cannot assign members to an incomplete role' : ''}
              >
                <UserPlus size={14} className="mr-1.5" /> Add Member
              </Button>
            </div>

            {selectedRoleForMembership?.is_incomplete && (
              <div className="bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-xs text-red-700 flex items-center gap-2 mt-2" data-testid="incomplete-role-warning">
                <Warning size={14} className="shrink-0" />
                <span>This role is incomplete and cannot be assigned. It requires {!(selectedRoleForMembership.entitlements?.length) ? 'entitlements' : ''}{!(selectedRoleForMembership.entitlements?.length) && !(selectedRoleForMembership.approval_workflow?.length) ? ' and ' : ''}{!(selectedRoleForMembership.approval_workflow?.length) ? 'an approval workflow' : ''} to be configured.</span>
              </div>
            )}

            <TabsContent value="active" className="flex-1 overflow-auto mt-0">
              <MembershipTable 
                assignments={roleAssignments.filter(a => a.status === 'active')} 
                statusColor={statusColor} 
                typeColor={typeColor}
                onRemove={handleRemoveMember}
                loading={loadingMembers}
              />
            </TabsContent>

            <TabsContent value="all" className="flex-1 overflow-auto mt-0">
              <MembershipTable 
                assignments={roleAssignments} 
                statusColor={statusColor} 
                typeColor={typeColor}
                onRemove={handleRemoveMember}
                loading={loadingMembers}
              />
            </TabsContent>
          </Tabs>

          <DialogFooter className="pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setShowMembershipDialog(false)} className="rounded-sm">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD MEMBER DIALOG */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Add Member to {selectedRoleForMembership?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest">Identity *</Label>
              <Popover open={memberIdentityPopoverOpen} onOpenChange={setMemberIdentityPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="mt-1 w-full justify-between rounded-sm h-10 font-normal" data-testid="add-member-identity-select">
                    {newMemberForm.identity_id ? (
                      <span className="flex items-center gap-2">
                        <Users size={14} className="text-navy-400" />
                        {getAvailableIdentities().find(i => i.id === newMemberForm.identity_id)?.display_name || 'Selected'}
                      </span>
                    ) : (
                      <span className="text-navy-400">Search and select an identity...</span>
                    )}
                    <CaretUpDown size={14} className="text-navy-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0" align="start">
                  <div className="p-2 border-b border-slate-200">
                    <div className="relative">
                      <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-navy-400" />
                      <Input placeholder="Search by name or email..." value={memberIdentitySearch} onChange={(e) => setMemberIdentitySearch(e.target.value)} className="pl-7 h-8 text-sm rounded-sm" />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {getAvailableIdentities().filter(i =>
                      !memberIdentitySearch || i.display_name?.toLowerCase().includes(memberIdentitySearch.toLowerCase()) || i.email?.toLowerCase().includes(memberIdentitySearch.toLowerCase())
                    ).slice(0, 50).map(i => (
                      <div key={i.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => { setNewMemberForm({...newMemberForm, identity_id: i.id}); setMemberIdentityPopoverOpen(false); setMemberIdentitySearch(''); }}>
                        <div className={`w-4 h-4 flex items-center justify-center ${newMemberForm.identity_id === i.id ? 'text-orange-500' : 'text-transparent'}`}>
                          <CheckCircle size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-navy-700 truncate">{i.display_name}</p>
                          {i.email && <p className="text-xs text-navy-400 truncate">{i.email}</p>}
                        </div>
                      </div>
                    ))}
                    {getAvailableIdentities().filter(i => !memberIdentitySearch || i.display_name?.toLowerCase().includes(memberIdentitySearch.toLowerCase()) || i.email?.toLowerCase().includes(memberIdentitySearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-4 text-center text-sm text-navy-400">No available identities found</div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              {getAvailableIdentities().length === 0 && (
                <p className="text-xs text-amber-600 mt-1">All identities are already members of this role.</p>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest">Expiry Date (Optional)</Label>
              <Popover open={memberCalendarOpen} onOpenChange={setMemberCalendarOpen} modal={false}>
                <PopoverTrigger asChild>
                  <Button variant="outline"
                    className={`mt-1 w-full justify-start text-left font-normal rounded-sm h-10 ${!newMemberForm.expires_at ? 'text-navy-400' : ''}`}
                    data-testid="add-member-expiry-trigger">
                    <CalendarBlank size={14} className="mr-2 text-navy-400" />
                    {newMemberForm.expires_at ? format(newMemberForm.expires_at, 'PPP') : 'No expiry — click to set'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[60]" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={newMemberForm.expires_at}
                    onSelect={(date) => { setNewMemberForm({...newMemberForm, expires_at: date}); setMemberCalendarOpen(false); }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                  {newMemberForm.expires_at && (
                    <div className="px-3 pb-3">
                      <Button variant="ghost" size="sm" onClick={() => { setNewMemberForm({...newMemberForm, expires_at: null}); setMemberCalendarOpen(false); }}
                        className="w-full text-xs text-red-500 hover:text-red-700">
                        Clear expiry date
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMemberDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleAddMember} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="confirm-add-member-button">
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Membership table component */
function MembershipTable({ assignments, statusColor, typeColor, onRemove, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowsClockwise size={24} className="animate-spin text-navy-400" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
      <Table wrapperClassName="max-h-[400px]">
        <TableHeader>
          <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Identity</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Type</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Status</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Expiry</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Assigned By</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Assigned At</TableHead>
            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {assignments.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-12 text-sm text-navy-400">
                <Users size={32} className="mx-auto mb-2 text-navy-300" />
                No members found.
              </TableCell>
            </TableRow>
          ) : assignments.map(item => (
            <TableRow key={item.id} className="hover:bg-slate-50 transition-colors" data-testid={`member-row-${item.id}`}>
              <TableCell className="font-medium text-sm text-navy-900">{item.identity_name}</TableCell>
              <TableCell>
                <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${typeColor(item.assignment_type)}`}>
                  {item.assignment_type}
                </span>
              </TableCell>
              <TableCell>
                <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${statusColor(item.status)}`}>
                  {item.status?.replace('_', ' ')}
                </span>
              </TableCell>
              <TableCell className="text-sm text-navy-600">
                {item.expires_at ? format(new Date(item.expires_at), 'MMM d, yyyy') : <span className="text-navy-400">No expiry</span>}
              </TableCell>
              <TableCell className="text-sm text-navy-600">{item.assigned_by}</TableCell>
              <TableCell className="text-sm text-navy-500">
                {item.created_at ? format(new Date(item.created_at), 'MMM d, yyyy') : '-'}
              </TableCell>
              <TableCell className="text-right">
                {item.status === 'active' && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onRemove(item.id)}
                    className="text-red-500 hover:text-red-700 text-xs h-7"
                    data-testid={`remove-member-${item.id}`}
                  >
                    <XCircle size={14} className="mr-1" /> Remove
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

